import React, { useContext, useState, useMemo, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeContext } from 'styled-components';
import { useQuery } from 'react-query';
import { getUsersTemplate } from '../../../services/template/Templates';
import { listTemplateParticipants } from '../../../services/template/Participants';
import { calculateTestSplits } from '../../../services/bill/BillRules';
import { CsrfContext } from '../../../providers/CsrfProvider';
import SectionHeader from './SectionHeader';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import FormatNumber from '../../../utils/formatters/FormatNumber';

function getBreakdownForUser(calculatedSplits, user) {
    if (!calculatedSplits?.rule_breakdowns || !user?.id) return [];
    const breakdown = [];
    calculatedSplits.rule_breakdowns.forEach((ruleBreakdown) => {
        const participantSplit = ruleBreakdown.participants?.find((p) => p.user_id === user.id);
        if (participantSplit != null) {
            breakdown.push({
                ruleType: ruleBreakdown.rule_type,
                amount: participantSplit.amount,
                ruleAmount: ruleBreakdown.total_applied,
            });
        }
    });
    return breakdown;
}

const TestSplitsModal = ({ open, onClose, templateId }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);

    const [inputValue, setInputValue] = useState('');
    const [testAmount, setTestAmount] = useState(null);
    const [expandedBreakdownUserId, setExpandedBreakdownUserId] = useState(null);

    const { data: billData } = useQuery(
        ['templates', String(templateId)],
        () => getUsersTemplate(String(templateId), csrfToken),
        { enabled: open && !!templateId && !!csrfToken }
    );

    const typicalCost = billData?.typical_cost || 0;

    useEffect(() => {
        if (open && typicalCost > 0) {
            setInputValue(String(typicalCost));
        }
        if (!open) {
            setTestAmount(null);
            setExpandedBreakdownUserId(null);
        }
    }, [open, typicalCost]);

    const { data: participants = [] } = useQuery(
        ['participants', String(templateId)],
        listTemplateParticipants(csrfToken),
        { enabled: open && !!templateId && !!csrfToken }
    );

    const { data: calculatedSplits, isLoading: isLoadingSplits } = useQuery(
        ['testSplitsModal', String(templateId), testAmount],
        () => calculateTestSplits(String(templateId), testAmount, csrfToken),
        {
            enabled: open && !!templateId && !!csrfToken && testAmount != null && testAmount > 0,
            refetchOnWindowFocus: false,
        }
    );

    const estimatedCostsMap = useMemo(() => {
        if (!calculatedSplits || !calculatedSplits.final_splits) return {};
        return calculatedSplits.final_splits;
    }, [calculatedSplits]);

    // Rows: one per entry in API final_splits (so row count always matches API); look up participant for display
    const splitsRows = useMemo(() => {
        if (!calculatedSplits?.final_splits || typeof calculatedSplits.final_splits !== 'object') return [];
        const parts = participants && Array.isArray(participants) ? participants : [];
        return Object.entries(calculatedSplits.final_splits).map(([userId, amount]) => {
            const user = parts.find((p) => p && (String(p.id) === String(userId)));
            return { userId, amount, user: user || { id: userId, email: null } };
        });
    }, [calculatedSplits?.final_splits, participants]);

    const handleCalculate = () => {
        const num = parseFloat(inputValue);
        if (!Number.isNaN(num) && num > 0) {
            setTestAmount(num);
        } else {
            setTestAmount(null);
        }
    };

    const isLoadingBill = open && !!templateId && !billData;

    const borderColor = theme.colors?.borderContrast || theme.colors?.border || 'rgba(255, 255, 255, 0.12)';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme.colors?.backgroundPrimary || theme.colors?.background,
                    color: theme.colors?.text,
                    borderRadius: '16px',
                    maxWidth: { xs: '95vw', sm: '600px' },
                    width: { xs: '95vw', sm: '70vw' },
                },
            }}
        >
            <DialogTitle sx={{ padding: 0 }}>
                <SectionHeader title="Test" borderRadius="16px 16px 0 0" margin={0} />
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: 2,
                    paddingLeft: 0,
                    paddingRight: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Scrollable content */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        paddingLeft: 3,
                        paddingRight: 3,
                    }}
                >
                    {isLoadingBill ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: theme.colors?.primary }} />
                        </Box>
                    ) : participants && participants.length === 0 ? (
                        <Typography sx={{ color: theme.colors?.text, opacity: 0.8 }}>
                            No participants yet. Add participants to see how rules split the amount.
                        </Typography>
                    ) : testAmount == null || testAmount <= 0 ? null : isLoadingSplits ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                            <CircularProgress size={28} sx={{ color: theme.colors?.primary }} />
                        </Box>
                    ) : (
                        <>
                            <TableContainer sx={{ width: '100%' }}>
                                <Table size="small" sx={{ border: 'none', width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: theme.colors?.brandSecondary, fontWeight: 600 }}>Estimates</TableCell>
                                            <TableCell align="right" sx={{ color: theme.colors?.text, fontWeight: 600 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {splitsRows.map(({ userId, amount, user }) => {
                                            const isBreakdownOpen = expandedBreakdownUserId === userId;
                                            const userBreakdown = getBreakdownForUser(calculatedSplits, user);
                                            return (
                                                <React.Fragment key={userId}>
                                                    <TestSplitsRow
                                                        user={user}
                                                        estimatedCost={amount}
                                                        theme={theme}
                                                        onToggleBreakdown={() => setExpandedBreakdownUserId((id) => (id === userId ? null : userId))}
                                                        isBreakdownOpen={isBreakdownOpen}
                                                    />
                                                    {isBreakdownOpen && (
                                                        <TableRow sx={{ borderTop: 'none' }}>
                                                            <TableCell colSpan={2} sx={{ verticalAlign: 'top', p: 0, borderTop: 'none' }}>
                                                                <Box sx={{ backgroundColor: theme.colors?.backgroundPrimary || theme.colors?.background, py: 1.5, px: 2 }}>
                                                                    {userBreakdown.length > 0 ? (
                                                                        <Table size="small" sx={{ width: '100%', '& .MuiTableCell-root': { borderBottom: 'none' }, '& .MuiTableRow-root': { borderBottom: 'none' } }}>
                                                                            <TableBody>
                                                                                {userBreakdown.map((item, index) => (
                                                                                    <TableRow key={index} sx={{ borderBottom: 'none' }}>
                                                                                        <TableCell sx={{ color: theme.colors?.text, borderBottom: 'none', py: 0.5 }}>
                                                                                            {item.ruleType === 'minus' ? 'Minus rule' : item.ruleType === 'percent' ? 'Percent rule' : 'Remainder (default)'}
                                                                                        </TableCell>
                                                                                        <TableCell align="right" sx={{ color: theme.colors?.text, borderBottom: 'none', py: 0.5 }}>
                                                                                            ${FormatNumber(item.amount)}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    ) : (
                                                                        <Typography variant="body2" sx={{ color: theme.colors?.text, opacity: 0.7 }}>
                                                                            No rules apply, or cost is split evenly.
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>

                {/* Bottom: input + Calculate (stacked, full width) */}
                <Box
                    sx={{
                        paddingTop: 3,
                        paddingBottom: 3,
                        paddingLeft: 3,
                        paddingRight: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <TextField
                        fullWidth
                        label="Amount ($)"
                        type="number"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
                        inputProps={{ min: 0, step: 0.01 }}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: theme.colors?.text,
                                '& fieldset': { borderColor },
                            },
                            '& .MuiInputLabel-root': { color: theme.colors?.borderContrast || theme.colors?.text },
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCalculate}
                        disabled={isLoadingSplits}
                        sx={{
                            backgroundColor: theme.colors?.primary || theme.colors?.brandSecondary,
                            color: theme.colors?.buttonText || '#fff',
                            borderRadius: '8px',
                        }}
                    >
                        {isLoadingSplits ? 'Calculating...' : 'Calculate'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

function TestSplitsRow({ user, estimatedCost, theme, onToggleBreakdown, isBreakdownOpen }) {
    // Backend: final_splits positive = owes, negative = receives
    const isOwing = estimatedCost != null && Number(estimatedCost) > 0;
    const isReceiving = estimatedCost != null && Number(estimatedCost) < 0;
    const rowBg = isReceiving
        ? (theme.colors?.success ? `${theme.colors.success}14` : 'rgba(76, 175, 80, 0.08)')
        : isOwing
        ? (theme.colors?.error ? `${theme.colors.error}14` : 'rgba(244, 67, 54, 0.08)')
        : 'transparent';
    const amountColor = isReceiving
        ? (theme.colors?.success || '#2e7d32')
        : isOwing
        ? (theme.colors?.error || '#c62828')
        : theme.colors?.text;
    const sign = isReceiving ? '+' : isOwing ? '−' : '';
    const displayAmount = estimatedCost != null ? `${sign}$${FormatNumber(Math.abs(Number(estimatedCost)))}` : '−';

    const handleBreakdownClick = (e) => {
        e.stopPropagation();
        onToggleBreakdown?.();
    };

    return (
        <TableRow sx={{ backgroundColor: rowBg }}>
            <TableCell sx={{ color: theme.colors?.text, ...(isBreakdownOpen && { borderBottom: 'none' }) }}>{user?.email ?? '−'}</TableCell>
            <TableCell align="right" sx={{ color: theme.colors?.text, ...(isBreakdownOpen && { borderBottom: 'none' }) }}>
                {estimatedCost !== null && estimatedCost !== undefined ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Typography
                            sx={{
                                color: amountColor,
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={handleBreakdownClick}
                        >
                            {displayAmount}
                        </Typography>
                        <Tooltip title={isBreakdownOpen ? 'Close breakdown' : 'Cost breakdown'}>
                            <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }} onClick={handleBreakdownClick}>
                                {isBreakdownOpen ? (
                                    <CloseIcon sx={{ color: amountColor, fontSize: '1.125rem', opacity: 0.9 }} />
                                ) : (
                                    <HelpOutlineIcon sx={{ color: amountColor, fontSize: '1rem', opacity: 0.8 }} />
                                )}
                            </Box>
                        </Tooltip>
                    </Box>
                ) : (
                    '−'
                )}
            </TableCell>
        </TableRow>
    );
}

export default TestSplitsModal;
